import {
  FuturesGridBots,
  SignalBots,
  SpotGridBots,
  TwapBots,
  TypesInstruments,
  TypesLeverageInfo,
  TypesMarginMode,
  TypesMarginMode_lower,
  TypesOrder,
  TypesPosition,
  TypesSpotSymbols,
  TypesSymbols,
  TypesTicker,
  TypesTickerBySpot,
  TypesTpslOrder,
  TypesTradeMode,
  TypesTrailingStopOrders,
  TypesTriggerOrder,
} from '@blofin/blofin-utils';
import { FuturesDcaBots, SpotDcaBots } from '@blofin/blofin-utils/types/union';
import { Dispatch, createContext, useEffect, useReducer } from 'react';
import { LanguagesType } from '../../packages/config';
import { Instruments, OrderUnitType, Ticker } from '../../packages/type';

export interface Fiat {
  name: string;
  rate: string;
  full_name: string;
  precision: string;
  symbol: string;
}

export interface Rates {
  usdt_usd: string;
  fiat: Fiat[];
}
export interface UserInfo {
  nick_name: string;
  profile: string;
  state: number;
  uid: string;
  uid_encryption: string;
  uid_str: string;
  user_level: number;
  api_key_permission: boolean;
  vip_level: number;
  account_type: string; //MAIN 主账户，NORMAL_SUB_ACCOUNT 普通子账户，
  switch_able: boolean;
  sub_allow_login: boolean;
  messages: {
    [key: string]: string;
  };
}

type TypesSyncPosLeverageInfo = (marketId: string, marginMode: TypesMarginMode_lower) => void;

export interface State {
  userInfo: UserInfo | null;
  baseUrl: string | null;
  http?: any | null;
  rates: Rates | undefined;
  lang?: LanguagesType;
  instruments?: Record<string, Instruments>;
  instrumentsByFutures?: Record<string, TypesInstruments>;
  symbols?: TypesSymbols[];
  symbolsKeyby?: Record<string, TypesSymbols>;
  symbolsBySpot?: TypesSpotSymbols[];
  symbolsKeybyBySpot?: Record<string, TypesSpotSymbols>;
  ticker?: Record<string, Ticker>;
  tickerByFutures?: Record<string, TypesTicker>;
  orderUnitType?: OrderUnitType;
  orderUnitTypeForCMP?: OrderUnitType;
  orderUnitTypeForUMP?: OrderUnitType;
  orderUnitTypeForUMC?: OrderUnitType;
  userViewType?: string;
  isDemoTrading?: boolean;
  userAccountType?: string;
  messages: {
    [key: string]: any;
  };
  positions?: TypesPosition[];
  orders?: TypesOrder[];
  newOrders?: TypesOrder[];
  tpslOrders?: TypesTpslOrder[];
  newTpslOrders?: TypesTpslOrder[];
  triggerOrders?: TypesTriggerOrder[];
  newTriggerOrders?: TypesTriggerOrder[];
  trailingStopOrders?: TypesTrailingStopOrders[];
  cancelAllOrdersOpen?: boolean;
  changeCurrency?: any;
  leverageInfo?: TypesLeverageInfo;
  signalBots?: SignalBots[];
  twapBots?: TwapBots[];
  closeAllPositionsOpen?: boolean;
  reversePositionsOpen?: boolean;
  actionPrice?: string;
  setActionPrice?: (price: string) => void;
  marginMode?: TypesMarginMode;
  tradeMode?: TypesTradeMode;
  syncPosLeverageInfo?: TypesSyncPosLeverageInfo;
  futuresGridBots?: FuturesGridBots[];
  futuresDcaBots?: FuturesDcaBots[];
  overviewAssets?: string;
  charts?: number;
  tickerBySpot?: Record<string, TypesTickerBySpot>;
  spotGridBots?: SpotGridBots[];
  spotDcaBots?: SpotDcaBots[];
  fundSources?: { type: string }[];
}

type Action =
  | {
      type: 'ADD_SYMBOLS';
      playload: { symbolsKeyby: Record<string, TypesSymbols>; symbols: TypesSymbols[] };
    }
  | {
      type: 'ADD_SYMBOLS_SPOT';
      playload: {
        symbolsKeybyBySpot: Record<string, TypesSpotSymbols>;
        symbolsBySpot: TypesSpotSymbols[];
      };
    }
  | { type: 'ADD_USER'; playload: UserInfo }
  | { type: 'ADD_INSTRUMENTS'; playload: Record<string, Instruments> }
  | { type: 'ADD_INSTRUMENTS_BY_FUTURES'; playload: Record<string, TypesInstruments> }
  | { type: 'ADD_TICKER'; playload: Record<string, Ticker> }
  | { type: 'ADD_TICKER_BY_FUTURES'; playload: Record<string, TypesTicker> }
  | { type: 'ADD_ORDER_UNIT_TYPE'; playload: OrderUnitType }
  | { type: 'ADD_ORDER_UNIT_TYPE_FOR_CMP'; playload: OrderUnitType }
  | { type: 'ADD_ORDER_UNIT_TYPE_FOR_UMP'; playload: OrderUnitType }
  | { type: 'ADD_ORDER_UNIT_TYPE_FOR_UMC'; playload: OrderUnitType }
  | { type: 'ADD_RATES'; playload: Rates }
  | { type: 'ADD_USER_VIEW_TYPE'; playload: string }
  | { type: 'ADD_IS_DEMO_TRADING'; playload: boolean }
  | {
      type: 'ADD_MESSAGES';
      playload: {
        [key: string]: string;
      };
    }
  | { type: 'ADD_POSITIONS'; playload: TypesPosition[] }
  | { type: 'ADD_ORDERS'; playload: TypesOrder[] }
  | { type: 'ADD_NEW_ORDERS'; playload: TypesOrder[] }
  | { type: 'ADD_TPSL_ORDERS'; playload: TypesTpslOrder[] }
  | { type: 'ADD_NEW_TPSL_ORDERS'; playload: TypesTpslOrder[] }
  | { type: 'ADD_TRIGGER_ORDERS'; playload: TypesTriggerOrder[] }
  | { type: 'ADD_NEW_TRIGGER_ORDERS'; playload: TypesTriggerOrder[] }
  | { type: 'ADD_CANCEL_ALL_ORDERS_OPEN'; playload: boolean }
  | { type: 'ADD_CHANGE_CURRENCY'; playload: any }
  | { type: 'ADD_LEVERAGE_INFO'; playload: TypesLeverageInfo }
  | { type: 'ADD_TRAILINGSTOP_ORDERS'; playload: TypesTrailingStopOrders[] }
  | { type: 'ADD_SIGNAL_BOTS'; playload: SignalBots[] }
  | { type: 'ADD_TWAP_BOTS'; playload: TwapBots[] }
  | { type: 'ADD_CLOSE_ALL_POSITIONS_OPEN'; playload: boolean }
  | { type: 'ADD_REVERSE_POSITIONS_OPEN'; playload: boolean }
  | { type: 'ADD_ACTION_PRICE'; playload: string }
  | { type: 'ADD_SET_ACTION_PRICE'; playload: (price: string) => void }
  | { type: 'ADD_MARGIN_MODE'; playload: TypesMarginMode | undefined }
  | { type: 'ADD_TRADE_MODE'; playload: TypesTradeMode }
  | { type: 'ADD_SYNC_POS_LEVERAGEINFO'; playload: TypesSyncPosLeverageInfo }
  | { type: 'ADD_FUTURES_GRID_BOTS'; playload: FuturesGridBots[] }
  | { type: 'ADD_FUTURES_DCA_BOTS'; playload: FuturesDcaBots[] }
  | { type: 'ADD_ASSETS'; playload: string }
  | { type: 'ADD_USER_ACCOUNT_TYPE'; playload: string }
  | { type: 'ADD_CHARTS'; playload: number }
  | { type: 'ADD_TICKER_BY_SPOT'; playload: Record<string, TypesTickerBySpot> }
  | { type: 'ADD_SPOT_GRID_BOTS'; playload: SpotGridBots[] }
  | { type: 'ADD_SPOT_DCA_BOTS'; playload: SpotDcaBots[] }
  | { type: 'ADD_FUND_SOURCES'; playload: { type: string }[] | undefined };

export interface StoreContextType {
  state: State;
  dispatch: Dispatch<Action>;
}

const BlofinContext = createContext<StoreContextType>({
  state: {} as State,
  dispatch: () => {},
});

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'ADD_SYMBOLS':
      return {
        ...state,
        symbols: action.playload.symbols,
        symbolsKeyby: action.playload.symbolsKeyby,
      };
    case 'ADD_SYMBOLS_SPOT':
      return {
        ...state,
        symbolsBySpot: action.playload.symbolsBySpot,
        symbolsKeybyBySpot: action.playload.symbolsKeybyBySpot,
      };
    case 'ADD_USER':
      return { ...state, userInfo: action.playload };
    case 'ADD_INSTRUMENTS':
      return { ...state, instruments: action.playload };
    case 'ADD_INSTRUMENTS_BY_FUTURES':
      return { ...state, instrumentsByFutures: action.playload };
    case 'ADD_TICKER':
      return { ...state, ticker: action.playload };
    case 'ADD_TICKER_BY_FUTURES':
      return { ...state, tickerByFutures: action.playload };
    case 'ADD_ORDER_UNIT_TYPE':
      return { ...state, orderUnitType: action.playload };
    case 'ADD_ORDER_UNIT_TYPE_FOR_CMP':
      return { ...state, orderUnitTypeForCMP: action.playload };
    case 'ADD_ORDER_UNIT_TYPE_FOR_UMP':
      return { ...state, orderUnitTypeForUMP: action.playload };
    case 'ADD_ORDER_UNIT_TYPE_FOR_UMC':
      return { ...state, orderUnitTypeForUMC: action.playload };
    case 'ADD_RATES':
      return { ...state, rates: action.playload };
    case 'ADD_USER_VIEW_TYPE':
      return { ...state, userViewType: action.playload };
    case 'ADD_IS_DEMO_TRADING':
      return { ...state, isDemoTrading: action.playload };
    case 'ADD_MESSAGES':
      return { ...state, messages: action.playload };
    case 'ADD_POSITIONS':
      return { ...state, positions: action.playload };
    case 'ADD_ORDERS':
      return { ...state, orders: action.playload };
    case 'ADD_NEW_ORDERS':
      return { ...state, newOrders: action.playload };
    case 'ADD_TPSL_ORDERS':
      return { ...state, tpslOrders: action.playload };
    case 'ADD_NEW_TPSL_ORDERS':
      return { ...state, newTpslOrders: action.playload };
    case 'ADD_NEW_TRIGGER_ORDERS':
      return { ...state, newTriggerOrders: action.playload };
    case 'ADD_TRIGGER_ORDERS':
      return { ...state, triggerOrders: action.playload };
    case 'ADD_CANCEL_ALL_ORDERS_OPEN':
      return { ...state, cancelAllOrdersOpen: action.playload };
    case 'ADD_CHANGE_CURRENCY':
      return { ...state, changeCurrency: action.playload };
    case 'ADD_LEVERAGE_INFO':
      return { ...state, leverageInfo: action.playload };
    case 'ADD_TRAILINGSTOP_ORDERS':
      return { ...state, trailingStopOrders: action.playload };
    case 'ADD_SIGNAL_BOTS':
      return { ...state, signalBots: action.playload };
    case 'ADD_TWAP_BOTS':
      return { ...state, twapBots: action.playload };
    case 'ADD_FUTURES_GRID_BOTS':
      return { ...state, futuresGridBots: action.playload };
    case 'ADD_CLOSE_ALL_POSITIONS_OPEN':
      return { ...state, closeAllPositionsOpen: action.playload };
    case 'ADD_REVERSE_POSITIONS_OPEN':
      return { ...state, reversePositionsOpen: action.playload };
    case 'ADD_ACTION_PRICE':
      return { ...state, actionPrice: action.playload };
    case 'ADD_SET_ACTION_PRICE':
      return { ...state, setActionPrice: action.playload };
    case 'ADD_MARGIN_MODE':
      return { ...state, marginMode: action.playload };
    case 'ADD_TRADE_MODE':
      return { ...state, tradeMode: action.playload };
    case 'ADD_SYNC_POS_LEVERAGEINFO':
      return { ...state, syncPosLeverageInfo: action.playload };
    case 'ADD_ASSETS':
      return { ...state, overviewAssets: action.playload };
    case 'ADD_FUTURES_DCA_BOTS':
      return { ...state, futuresDcaBots: action.playload };
    case 'ADD_USER_ACCOUNT_TYPE':
      return { ...state, userAccountType: action.playload };
    case 'ADD_CHARTS':
      return { ...state, charts: action.playload };
    case 'ADD_TICKER_BY_SPOT':
      return { ...state, tickerBySpot: action.playload };
    case 'ADD_SPOT_GRID_BOTS':
      return { ...state, spotGridBots: action.playload };
    case 'ADD_SPOT_DCA_BOTS':
      return { ...state, spotDcaBots: action.playload };
    case 'ADD_FUND_SOURCES':
      return { ...state, fundSources: action.playload };
    default:
      return state;
  }
};

const BlofinProvider = ({ children, value }: { children: React.ReactNode; value: State }) => {
  const [state, dispatch] = useReducer(reducer, value);

  useEffect(() => {
    if (value.userInfo) {
      dispatch({ type: 'ADD_USER', playload: value.userInfo });
    }
    if (value.rates) {
      dispatch({ type: 'ADD_RATES', playload: value.rates });
    }
    if (value.messages) {
      dispatch({ type: 'ADD_MESSAGES', playload: value.messages });
    }
    if (value.userAccountType) {
      dispatch({ type: 'ADD_USER_ACCOUNT_TYPE', playload: value.userAccountType });
    }
  }, [value]);

  return <BlofinContext.Provider value={{ state, dispatch }}>{children}</BlofinContext.Provider>;
};

const getUserViewType = (cookieUserViewType?: string) => {
  const userViewType = cookieUserViewType || 'real-trading';
  return {
    userViewType: userViewType,
    isDemoTrading: Boolean(userViewType === 'demo-trading'),
  };
};

export { BlofinContext, BlofinProvider, getUserViewType };
